import { flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Button, Table } from "react-bootstrap";
import getMyEngagementHistory from "../../Helpers/MeApi";
import HistoryModal from "./HistoryModal";
import HistoryPagination from "./HistoryPagination";
import HistorySearch from "./HistorySearch";

const Dashboard = () => {
	const [history, setHistory] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [selectedRow, setSelectedRow] = useState();
	const [noRecordsFound, setNoRecordsFound] = useState(false);

	const handleClose = () => setShowModal(false);

	const handleShow = (row) => {
		setSelectedRow(row);
		setShowModal(true);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: surpressed as fix generated another error
	const columns = useMemo(
		() => [
			{
				header: "Course Code",
				accessorKey: "activityCode",
			},
			{
				header: "Instance Code",
				accessorKey: "instanceCode",
			},
			{
				header: "Course Start Date",
				accessorKey: "courseStartDate",
				meta: { className: "d-none d-sm-table-cell" },
				cell: (info) => moment(info.getValue()).format("DD/MM/yyyy"),
			},
			{
				header: "Course Title",
				accessorKey: "courseTitle",
				meta: { className: "d-none d-sm-table-cell" },
			},
			{
				header: "Survey Name",
				accessorKey: "surveyName",
				meta: { className: "d-none d-sm-table-cell" },
			},
			{
				header: "Sent On",
				accessorKey: "surveySentOn",
				meta: { className: "d-none d-sm-table-cell" },
				cell: (info) => moment(info.getValue()).format("DD/MM/yyyy"),
			},
			{
				header: "",
				id: "action-link",
				enableSorting: false,
				cell: ({ row }) => (
					<Button variant="primary" size="sm" onClick={() => handleShow(row.original)} className="btn-view-details">
						View Details
					</Button>
				),
			},
		],
		[],
	);

	useEffect(() => {
		async function getHistory() {
			const data = await getMyEngagementHistory();
			setHistory(data);
			setNoRecordsFound(data.length === 0);
		}
		getHistory();
	}, []);

	const [sorting, setSorting] = useState([
		{
			id: "surveySentOn",
			desc: false,
		},
	]);

	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: 20,
	});

	const { getHeaderGroups, getRowModel, getPageCount, getPageOptions, getCanNextPage, getCanPreviousPage, setPageIndex, nextPage, previousPage, getState } =
		useReactTable({
			columns,
			data: history,
			state: {
				sorting,
				pagination,
			},
			onSortingChange: setSorting,
			onPaginationChange: setPagination,
			getCoreRowModel: getCoreRowModel(),
			getSortedRowModel: getSortedRowModel(),
			getPaginationRowModel: getPaginationRowModel(),
			autoResetPageIndex: false,
		});

	const showSortIcon = (column) => {
		const sortState = column.getIsSorted();

		if (sortState) {
			if (sortState === "desc") {
				return " 🔽";
			}

			return " 🔼";
		}

		return "";
	};

	const initialSearchValues = {
		courseTitle: "",
		surveyName: "",
		courseStartDate: "",
	};

	const onSubmit = async (searchCriteria) => {
		const data = await getMyEngagementHistory(searchCriteria.courseTitle, searchCriteria.surveyName, searchCriteria.courseStartDate);
		setHistory(data);
		setNoRecordsFound(data.length === 0);
	};

	return (
		<div>
			<div className="intro-text">
				<p>
					Share your experience in our evaluation form to help us develop and improve our CPD. For some courses you may also be asked to make an action plan and
					reflect on the impact of your CPD after the course. These forms will also appear on your dashboard.
				</p>
			</div>
			<div className="FormWrapper">
				<HistorySearch onSubmit={onSubmit} initialValues={initialSearchValues} />
			</div>
			<div className="table-wrapper">
				<HistoryModal show={showModal} handleClose={handleClose} history={selectedRow} />
				<Table striped bordered hover>
					<thead>
						{getHeaderGroups().map((headerGroup) => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<th
										key={header.id}
										className={header.column.columnDef.meta?.className}
										onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
										role={header.column.getCanSort() ? "button" : undefined}
									>
										{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
										{header.column.getCanSort() && <span>{showSortIcon(header.column)}</span>}
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody>
						{noRecordsFound ? (
							<tr>
								<td colSpan={10}>
									<span className="d-block invalid-feedback">No engagement records were found</span>
								</td>
							</tr>
						) : (
							getRowModel().rows.map((row) => {
								return (
									<tr key={row.id}>
										{row.getVisibleCells().map((cell) => (
											<td key={cell.id} className={cell.column.columnDef.meta?.className}>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</td>
										))}
									</tr>
								);
							})
						)}
					</tbody>
				</Table>
				<HistoryPagination
					gotoPage={setPageIndex}
					previousPage={previousPage}
					nextPage={nextPage}
					canPreviousPage={getCanPreviousPage()}
					canNextPage={getCanNextPage()}
					pageCount={getPageCount()}
					pageOptions={getPageOptions()}
					pageIndex={getState().pagination.pageIndex}
				/>
			</div>
		</div>
	);
};

export default Dashboard;
